import { fetcher, FetchError, useQuery } from '@oaknorth/util-rest';
import { getHostname } from '@oaknorth/util-routing';

interface Auth0TenantInfo {
  clientId: string;
  domain: string;
  organizationMap: { [key: string]: string };
  connectionMap: { [key: string]: string };
}

export interface Auth0ProviderConfigBase {
  clientId: string;
  domain: string;
  audience: string;
  scope: string;
  organization: string | null;
  connection: string | null;
  logoutReturnTo: string | undefined;
}

export interface Auth0ProviderConfig {
  config: Auth0ProviderConfigBase | null;
  isLoading: boolean;
}

const AUDIENCE = 'onci_api';
const SCOPE = 'profile email website_user';

const getOrganization = (config: Auth0TenantInfo | undefined) => {
  if (config === undefined) {
    return null;
  }

  const hostname = getHostname();

  return config.organizationMap && hostname in config.organizationMap ? config.organizationMap[hostname] : null;
};

const getConnection = (config: Auth0TenantInfo | undefined) => {
  if (config === undefined) {
    return null;
  }

  const hostname = getHostname();

  return config.connectionMap && hostname in config.connectionMap ? config.connectionMap[hostname] : null;
};

const getLogoutReturnTo = (config: Auth0TenantInfo | undefined) => {
  if (config === undefined) {
    return undefined;
  }

  const hostname = getHostname();

  return config.organizationMap && hostname in config.organizationMap ? 'https://' + hostname : undefined;
};

export function useAuth0Config(): Auth0ProviderConfig {
  const path = '/api/user-authz/api/config.json';

  const getAuth0Config = useQuery<Auth0TenantInfo, FetchError>({
    queryKey: [path],
    queryFn: () =>
      fetcher<Auth0TenantInfo>({
        url: path,
      }),
  });

  if (getAuth0Config.error) {
    return { config: null, isLoading: getAuth0Config.isPending };
  }

  const config = getAuth0Config.data;

  if (!config) {
    return { config: null, isLoading: getAuth0Config.isLoading };
  }

  const organization = getOrganization(config);
  const connection = getConnection(config);
  const logoutReturnTo = getLogoutReturnTo(config);

  const providerConfig = {
    clientId: config.clientId,
    domain: config.domain,
    organization,
    audience: AUDIENCE,
    scope: SCOPE,
    connection,
    logoutReturnTo,
  };

  return { config: providerConfig, isLoading: getAuth0Config.isLoading };
}
